import { Fragment, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import DeleteIcon from "@mui/icons-material/Delete";
import apiService from "@/api/ApiService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useDropzone } from "react-dropzone";
import { Delete, PostAdd } from "@mui/icons-material";

type UploadPayload = {
  files: File[];
  description: string;
};

type UploadState = {
  total: number;
  currentIndex: number;
  currentFileProgress: number;
};

const baseStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "20px",
  backgroundColor: "#fafafa",
  color: "#bdbdbd",
  outline: "none",
  transition: "border .24s ease-in-out",
};

const focusedStyle = {
  backgroundColor: "#eaeaea",
};

const acceptStyle = {
  backgroundColor: "rgb(223, 255, 205)",
};

const rejectStyle = {
  backgroundColor: "rgb(255, 205, 205)",
};

export function GalleryUploader({ projectId, open, onClose }: { projectId: number; open: boolean; onClose: () => void }) {
  const [files, setFiles] = useState<File[]>([]);
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [uploadState, setUploadState] = useState<UploadState | null>(null);

  const queryClient = useQueryClient();

  const addFiles = (newFiles: File[]) => {
    const imageFiles = newFiles.filter((f) => f.type.startsWith("image/"));
    if (imageFiles.length === 0) return;
    setFiles((prev) => [...prev, ...imageFiles]);
  };

  const mutation = useMutation<void, unknown, UploadPayload>({
    mutationFn: async ({ files: selectedFiles, description }) => {
      setError(null);
      setUploadState(null);
      const create = apiService.createEntity("projectImage");

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];

        setUploadState({
          total: selectedFiles.length,
          currentIndex: i,
          currentFileProgress: 0,
        });

        const base64 = await fileToBase64(file);
        const formData = new FormData();
        formData.append("b64file", base64);
        formData.append("thumbnail", "true");
        formData.append("b64name", file.name);

        const uploadResult = await apiService.uploadFileApp({
          onProgress: (fraction: number) => {
            setUploadState({
              total: selectedFiles.length,
              currentIndex: i,
              currentFileProgress: fraction,
            });
          },
        })(formData);

        const payload: Partial<ProjectImage> = {
          project: projectId,
          description: description + (description ? " " : "") + (selectedFiles.length > 1 ? `(${i + 1}/${selectedFiles.length})` : ""),
          filename: uploadResult.file,
          thumbnail: uploadResult.thumbnail,
          originalFilename: file.name,
          madeAt: uploadResult.createdAt ?? null,
        };

        await create(payload as any);

        setUploadState({
          total: selectedFiles.length,
          currentIndex: i,
          currentFileProgress: 1,
        });
      }
    },
    onSuccess: () => {
      close();
      queryClient.invalidateQueries({ queryKey: ["getProjectImages", String(projectId)] });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Upload fehlgeschlagen");
      setUploadState(null);
    },
  });

  const close = () => {
    setFiles([]);
    setDescription("");
    setUploadState(null);
    onClose();
  };

  const previews = useMemo(
    () =>
      files.map((file) => ({
        name: file.name,
        url: URL.createObjectURL(file),
      })),
    [files],
  );

  const removeFile = (name: string) => {
    setFiles((prev) => prev.filter((f) => f.name !== name));
  };

  const { getRootProps, getInputProps, isDragActive, isFocused, isDragAccept, isDragReject } = useDropzone({
    disabled: mutation.isPending,
    accept: { "image/*": [] },
    onDrop: (acceptedFiles) => addFiles(acceptedFiles),
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject],
  );

  return (
    <Dialog open={open} maxWidth="md" onClose={close} fullWidth>
      <DialogTitle
        sx={{
          p: 1,
          px: 2,
          background: "#002169",
          color: "white",
          borderBottom: "1px solid rgba(255,255,255,0.5)",
        }}
      >
        Fotos hochladen
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            p: 2,
            overflow: "auto",
            gap: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Paper variant="outlined" {...getRootProps({ style })}>
            <input {...getInputProps()} />
            <Box sx={{ textAlign: "center", color: "#444" }}>
              <PostAdd />
              <Box>Fotos hier ablegen</Box>
            </Box>
          </Paper>

          {files.length > 0 && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
                gap: 1,
              }}
            >
              {previews.map((preview) => (
                <Box
                  sx={{
                    position: "relative",
                    borderRadius: 1,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "grey.200",
                    aspectRatio: "1 / 1",
                  }}
                  key={preview.url}
                >
                  <Box component="img" src={preview.url} alt={preview.name} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  <Box
                    sx={{
                      "*:hover>&": { opacity: 0.8 },
                      position: "absolute",
                      opacity: 0,
                      background: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                    }}
                  >
                    <IconButton size="large" onClick={() => removeFile(preview.name)} disabled={mutation.isPending}>
                      <Delete />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          <TextField
            label="Beschreibung"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            placeholder="Kurze Beschreibung für die neuen Fotos"
          />

          {error && <Alert severity="error">{error}</Alert>}

          {uploadState && (
            <Stack spacing={0.5}>
              <Typography variant="body2">
                Upload {uploadState.currentIndex + 1} / {uploadState.total}
              </Typography>
              <LinearProgress value={uploadState.currentFileProgress * 100} variant="determinate" />
            </Stack>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ borderTop: "1px solid #aaa" }}>
        <Button onClick={close}>Abbrechen</Button>

        <Button
          variant="contained"
          color="primary"
          disabled={files.length === 0 || mutation.isPending}
          onClick={() => mutation.mutate({ files, description })}
        >
          {mutation.isPending ? <CircularProgress size={24} /> : "Hochladen"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

async function fileToBase64(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
